import { useEffect, useState } from 'react';
import { Button, Check, Close, Image, Modal, Skeleton } from '../../../modules/design-system';
import { getInventory, type ApiClient, type IframePoolOrderItem } from '../../../modules/api';

export interface ItemPickerModalProps {
  client: ApiClient;
  /** Items already in the shipment — pre-selected when the picker opens AND
   *  carried through `onApply` so a still-checked item that isn't on the
   *  fetched inventory page (pagination) is never silently dropped. */
  selectedItems: IframePoolOrderItem[];
  /** Apply the chosen items and close. */
  onApply: (items: IframePoolOrderItem[]) => void;
  onClose: () => void;
}

/**
 * "Select Items" picker (web parity) — lets the player choose which of their
 * cards go in the shipment WITHOUT leaving the delivery page. Loads the
 * session inventory, multi-selects by card tap, and hands the chosen items
 * back on Done.
 */
export function ItemPickerModal({ client, selectedItems, onApply, onClose }: ItemPickerModalProps) {
  const [items, setItems] = useState<IframePoolOrderItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState<Set<string>>(
    () => new Set(selectedItems.map((it) => it.id)),
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void getInventory(client, { per: 60 })
      .then((page) => {
        if (!cancelled) setItems(page.items);
      })
      .catch(() => {
        /* leave empty → "no items" state */
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [client]);

  const toggle = (id: string) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const apply = () => {
    // Resolve each picked id from BOTH the fetched page and the pre-selected
    // items, so a still-checked item that isn't on this page (pagination /
    // a different inventory fetch) survives instead of being dropped.
    const byId = new Map<string, IframePoolOrderItem>();
    for (const it of selectedItems) byId.set(it.id, it);
    for (const it of items) byId.set(it.id, it);
    const result: IframePoolOrderItem[] = [];
    for (const id of picked) {
      const it = byId.get(id);
      if (it) result.push(it);
    }
    onApply(result);
  };

  return (
    <Modal
      onClose={onClose}
      inspectName="ItemPickerModal"
      ariaLabelledBy="item-picker-title"
      widthClassName="max-w-[640px]"
    >
      <div
        className="relative flex max-h-[88vh] w-full flex-col rounded-[28px] p-6 text-white"
        style={{ background: '#15181E' }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full transition-opacity hover:opacity-80 active:scale-95"
          style={{ background: 'rgba(255,255,255,0.08)', color: '#FFFFFF' }}
        >
          <Close size={18} strokeWidth={2} />
        </button>

        <h2 id="item-picker-title" className="text-[20px] font-bold tracking-[-0.3px]">
          Select Items
        </h2>
        <p className="mb-4 mt-1 text-[13px]" style={{ color: 'rgba(255,255,255,0.55)' }}>
          Choose the cards to include in this shipment.
        </p>

        <div className="min-h-0 flex-1 overflow-y-auto [scrollbar-gutter:stable]">
          {loading ? (
            // One row of card-shaped placeholders that mirror the real tile
            // (image + 2-line title + same padding/border), so the layout
            // height is stable and nothing jumps when the cards load in.
            <div className="grid grid-cols-3 gap-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          ) : items.length === 0 ? (
            <p className="py-10 text-center text-[14px]" style={{ color: 'rgba(255,255,255,0.55)' }}>
              No items available to ship.
            </p>
          ) : (
            <ul className="grid grid-cols-3 gap-3">
              {items.map((it) => {
                const isPicked = picked.has(it.id);
                return (
                  <li key={it.id}>
                    <button
                      type="button"
                      onClick={() => toggle(it.id)}
                      aria-pressed={isPicked}
                      className="flex w-full flex-col gap-2 rounded-[16px] p-2 text-left transition-colors"
                      style={{
                        border: '1.5px solid ' + (isPicked ? '#23D11D' : 'rgba(255,255,255,0.08)'),
                        background: isPicked ? 'rgba(35,209,29,0.08)' : 'rgba(255,255,255,0.03)',
                      }}
                    >
                      <div
                        className="relative aspect-[3/4] w-full overflow-hidden rounded-[12px]"
                        style={{ background: 'rgba(255,255,255,0.04)' }}
                      >
                        <Image src={it.imageUrl} alt={it.title} className="h-full w-full object-cover" />
                        <span
                          className="absolute right-1.5 top-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full"
                          style={
                            isPicked
                              ? { background: '#23D11D', color: '#0A0A0A' }
                              : { border: '1.5px solid rgba(255,255,255,0.5)', background: 'rgba(0,0,0,0.35)' }
                          }
                        >
                          {isPicked && <Check size={13} strokeWidth={3} />}
                        </span>
                      </div>
                      <div className="line-clamp-2 min-h-[32px] text-[12px] font-medium leading-[16px]">
                        {it.title}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="mt-5 flex items-center gap-3">
          <Button variant="glass-frosted" size="lg" onClick={onClose} fullWidth>
            Cancel
          </Button>
          <Button variant="primary" size="lg" onClick={apply} fullWidth>
            {picked.size > 0 ? `Done (${picked.size})` : 'Done'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Loading placeholder shaped exactly like a real card tile — same padding,
 * border, image aspect and a 32px (two-line, `leading-16`) title block — so a
 * row of these occupies the same height the cards will, preventing any layout
 * shift when the inventory resolves.
 */
function SkeletonCard() {
  return (
    <div
      className="flex w-full flex-col gap-2 rounded-[16px] p-2"
      style={{
        border: '1.5px solid rgba(255,255,255,0.08)',
        background: 'rgba(255,255,255,0.03)',
      }}
    >
      <Skeleton className="aspect-[3/4] w-full rounded-[12px]" />
      <div className="flex flex-col gap-2">
        <Skeleton className="h-[12px] w-full rounded" />
        <Skeleton className="h-[12px] w-2/3 rounded" />
      </div>
    </div>
  );
}
